import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, Tag } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin → Categories tab. Self-contained CRUD for content categories.
 *
 * Parent (AdminPage) may pass `onChange` to be notified when the list
 * mutates — currently used to refresh the category dropdown shown in the
 * Add/Edit Content dialog.
 */
const CategoriesTab = ({ onChange }) => {
  const [categories, setCategories] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({ id: '', name: '' });
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/categories`, { withCredentials: true });
      setCategories(res.data);
    } catch {
      console.error('Failed to fetch categories');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleAdd = async () => {
    if (!form.id || !form.name) {
      toast.error('Both ID and name are required');
      return;
    }
    try {
      await axios.post(`${API}/api/admin/categories`, form, { withCredentials: true });
      toast.success('Category created');
      setForm({ id: '', name: '' });
      setIsOpen(false);
      await fetchCategories();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete category "${id}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/categories/${id}`, { withCredentials: true });
      toast.success('Category deleted');
      await fetchCategories();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading categories...</div>;

  return (
    <div data-testid="categories-tab-content">
      <div className="flex justify-end mb-6">
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <button
              className="px-4 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors flex items-center gap-2"
              data-testid="add-category-btn"
            >
              <Plus className="w-5 h-5" />
              Add Category
            </button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Add Category</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Category ID *</Label>
                <Input
                  value={form.id}
                  onChange={(e) => setForm({ ...form, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  className="bg-slate-800 border-white/10 mt-1"
                  placeholder="e.g. solo, milf, trans"
                />
                <p className="text-xs text-slate-500 mt-1">Lowercase, no spaces (used for filtering)</p>
              </div>
              <div>
                <Label>Display Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-slate-800 border-white/10 mt-1"
                  placeholder="e.g. Solo, MILF, Trans"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setIsOpen(false)}
                  className="flex-1 py-3 border border-white/10 text-white rounded-full font-medium hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  className="flex-1 py-3 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600"
                  data-testid="confirm-add-category-btn"
                >
                  Create
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.length === 0 ? (
          <div className="col-span-full text-center py-12 glass-light rounded-2xl">
            <Tag className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500">No categories found</p>
            <p className="text-slate-600 text-sm">Add categories or seed sample data</p>
          </div>
        ) : (
          categories.map((cat) => (
            <div key={cat.id} className="glass-light rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-white font-medium">{cat.name}</p>
                <p className="text-sm text-slate-500">ID: {cat.id} • {cat.count} items</p>
              </div>
              <button
                onClick={() => handleDelete(cat.id)}
                className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-500/10 rounded-lg"
                data-testid={`delete-category-${cat.id}`}
              >
                <Trash className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CategoriesTab;
